import { z } from 'zod';

export const loginSchema = z.object({
  email: z.string().email('Correo electrónico inválido'),
  password: z.string().min(6, 'La contraseña debe tener al menos 6 caracteres'),
});

export const clientSchema = z.object({
  name: z.string().min(2, 'El nombre debe tener al menos 2 caracteres'),
  company: z.string().optional(),
  email: z.string().email('Correo electrónico inválido').optional().or(z.literal('')),
  phone: z.string().optional(),
  address: z.string().optional(),
  notes: z.string().optional(),
});

export const cctvSchema = z.object({
  clientId: z.string().uuid('Cliente inválido'),
  brand: z.string().min(1, 'La marca es requerida'),
  model: z.string().optional(),
  serialNumber: z.string().optional(),
  location: z.string().optional(),
  installationDate: z.string().optional(),
  status: z.enum(['active', 'inactive', 'maintenance']),
  ipAddress: z
    .string()
    .regex(/^(\d{1,3}\.){3}\d{1,3}$/, 'Dirección IP inválida')
    .optional()
    .or(z.literal('')),
  port: z
    .number()
    .int()
    .min(1, 'Puerto debe estar entre 1 y 65535')
    .max(65535, 'Puerto debe estar entre 1 y 65535')
    .optional(),
  notes: z.string().optional(),
});

export const maintenanceSchema = z.object({
  clientId: z.string().uuid('Cliente inválido'),
  cctvId: z.string().uuid().optional().or(z.literal('')),
  type: z.enum(['preventive', 'corrective']),
  description: z.string().min(10, 'La descripción debe tener al menos 10 caracteres'),
  status: z.enum(['pending', 'in-progress', 'completed', 'cancelled']),
  priority: z.enum(['low', 'normal', 'high', 'critical']),
  scheduledDate: z.string().optional(),
  technician: z.string().optional(),
  cost: z.number().min(0, 'El costo no puede ser negativo'),
  notes: z.string().optional(),
});

export const userSchema = z.object({
  name: z.string().min(2, 'El nombre debe tener al menos 2 caracteres'),
  email: z.string().email('Correo electrónico inválido'),
  password: z
    .string()
    .min(8, 'La contraseña debe tener al menos 8 caracteres')
    .regex(/[A-Z]/, 'Debe contener al menos una mayúscula')
    .regex(/[a-z]/, 'Debe contener al menos una minúscula')
    .regex(/[0-9]/, 'Debe contener al menos un número'),
  role: z.enum(['admin', 'technician', 'viewer']),
});

export const changePasswordSchema = z
  .object({
    currentPassword: z.string().min(1, 'La contraseña actual es requerida'),
    newPassword: z
      .string()
      .min(8, 'La nueva contraseña debe tener al menos 8 caracteres')
      .regex(/[A-Z]/, 'Debe contener al menos una mayúscula')
      .regex(/[a-z]/, 'Debe contener al menos una minúscula')
      .regex(/[0-9]/, 'Debe contener al menos un número'),
    confirmPassword: z.string().min(1, 'Confirmar contraseña es requerido'),
  })
  .refine((data) => data.newPassword === data.confirmPassword, {
    message: 'Las contraseñas no coinciden',
    path: ['confirmPassword'],
  });

export type LoginFormData = z.infer<typeof loginSchema>;
export type ClientFormData = z.infer<typeof clientSchema>;
export type CCTVFormData = z.infer<typeof cctvSchema>;
export type MaintenanceFormData = z.infer<typeof maintenanceSchema>;
export type UserFormData = z.infer<typeof userSchema>;
export type ChangePasswordFormData = z.infer<typeof changePasswordSchema>;
